import './IntercomButton.scss';
import classNames from 'classnames';
import React, { FC, useEffect, useMemo } from 'react';
import SwiggyChatbotIcon from '../../../assets/images/swiggy_chatbot.png';
import { ElementIds } from '../../../ids';
import {
  useFlag,
  useGlobalState,
  useTimeoutCreator,
  useUserSafe,
} from '../../../scripts/hooks';
import { useContrast } from '../../../scripts/hooks/colors';
import { UIButton } from '../ui/UIButton/UIButton';
import { UIIcon } from '../ui/UIIcon/UIIcon';
import { bootstrapIntercomCode } from './intercomFallback';

/*
 * #OpenIntercomButton corresponds to the Intercom help button.
 * This should match the Inbox Button selector set in Segment's Intercom connection:
 * https://app.segment.com/dashworks/destinations/intercom/sources/dashboard-electron-app/instances/609467db1cc5a4b145568794/configuration
 * References:
 * https://segment.com/docs/connections/destinations/catalog/intercom/#custom-inbox-button-selector
 * https://www.intercom.com/help/en/articles/2894-customize-the-intercom-messenger-technical
 */

export const IntercomButton: FC = () => {
  const userName = useGlobalState((state) => state.meta.userDisplayName);
  const user = useUserSafe();
  const userInfo = useMemo(
    () => ({
      name: userName ?? 'n/a',
      id: user.userId,
      email: user.email,
    }),
    [userName, user.userId, user.email]
  );

  return (
    <PlainIntercomButton
      className={classNames({
        contrast: useContrast(),
      })}
      userInfo={userInfo}
    />
  );
};

const enum IntercomLoadState {
  NotLoaded,
  AutoLoaded,
  ManuallyLoaded,
}

let intercomLoadState = IntercomLoadState.NotLoaded;

declare const chatteron: { init: (arg0: { clientId: string }) => void };

export const PlainIntercomButton: FC<{
  className?: string;
  userInfo?: {
    name: string;
    email: string;
    id: string;
  };
}> = ({ className, userInfo }) => {
  const setTimeout = useTimeoutCreator();
  const customChatbot = useFlag('customChatbot');
  useEffect(() => {
    setTimeout(() => {
      const isLoaded = document.getElementById('intercom-frame');
      switch (intercomLoadState) {
        case IntercomLoadState.AutoLoaded:
          return;
        case IntercomLoadState.NotLoaded:
          if (isLoaded) {
            intercomLoadState = IntercomLoadState.AutoLoaded;
            return;
          }

          bootstrapIntercomCode(userInfo?.id, userInfo?.email, userInfo?.name);
          intercomLoadState = IntercomLoadState.ManuallyLoaded;
          return;
        case IntercomLoadState.ManuallyLoaded:
          // update
          bootstrapIntercomCode(userInfo?.id, userInfo?.email, userInfo?.name);
      }
    }, 1000);
  }, [setTimeout, userInfo]);

  useEffect(() => {
    const leenaScript = document.getElementById('leena-script');

    if (!leenaScript && customChatbot) {
      const script = document.createElement('script', {});

      script.addEventListener('load', () => {
        chatteron.init({
          clientId: CHATTERON_CLIENT_ID,
        });
      });

      script.src = 'https://web.leena.ai/scripts/sdk.js';
      script.id = 'leena-script';
      script.async = true;
      document.body.append(script);

      // HACK: This is to make sure a 'load' event is fired after the script is loaded.
      const interval = setInterval(() => {
        const elements = document.getElementsByClassName('chatBubble');
        if (elements.length > 0) {
          dispatchEvent(new Event('load'));
          clearInterval(interval);
        }

        const botImageElements = document.getElementsByClassName('botImage');
        if (botImageElements.length > 0 && botImageElements[0]) {
          const image = botImageElements[0] as HTMLImageElement;
          image.src = SwiggyChatbotIcon;
        }
      }, 500);
    } else if (leenaScript && !customChatbot) {
      leenaScript.remove();
      location.reload();
    }
  }, [customChatbot]);

  return customChatbot ? null : (
    <UIButton
      className={classNames('intercomButton', className)}
      id={ElementIds.OpenIntercomButton}
      propagate
      type="secondary"
    >
      <UIIcon name="chat" size={25} />
    </UIButton>
  );
};
